<template>
  <div class="journal-layout">
    <router-link to="/about" class="about-btn">About</router-link>
    <div class="journal-image-container">
      <div class="journal-image-wrapper">
        <img
          :src="background"
          alt="Journal Visual"
          class="journal-image"
          :class="{ 'fade-opacity': fadeStarted }"
          @load="onImageLoad"
          @error="onImageError"
        />
        <textarea
          v-model="journalEntry"
          class="journal-textarea-overlay"
          :placeholder="placeholderText"
        ></textarea>
      </div>
    </div>
    <button class="set-free-btn" @click="onSetFree">Set it Free</button>
      <div v-if="etsyUrl" class="etsy-label">
        Artwork: {{ etsyUrl }}
      </div>
    <transition name="fade-dialog">
      <div v-if="showDialog" class="fade-dialog-overlay" @click.self="closeDialog">
        <div class="fade-dialog-content">
          <p>
            <span class="parisienne-dialog">It's gone.</span><br>
            <span class="quicksand-dialog">Take a moment to thank yourself for letting go of what no longer serves you.</span>
          </p>
          <button class="close-dialog-btn" @click="closeDialog">Close</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>

import { defineComponent, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';

export default defineComponent({
  name: 'JournalPage',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const journalEntry = ref('');
    const placeholderText = ref("What's on your mind?");
    const fadeStarted = ref(false);
    const showDialog = ref(false);
    // Get query params for image and etsy url
    const background = route.query.image || '';
    const etsyUrl = route.query.etsy || '';
    const onImageLoad = () => {
      setTimeout(() => {
        fadeStarted.value = true;
      }, 0);
    };
    const onImageError = () => {
      // Redirect to PaperBoat page (assumed to be '/')
      router.replace('/');
    };
    const clearJournal = () => {
      journalEntry.value = '';
      placeholderText.value = '';
    };
    const onSetFree = () => {
      clearJournal();
      setTimeout(() => {
        showDialog.value = true;
      }, 200); // slight delay for effect
    };
    const closeDialog = () => {
      showDialog.value = false;
    };
    return {
      background,
      etsyUrl,
      journalEntry,
      placeholderText,
      fadeStarted,
      onImageLoad,
      onImageError,
      onSetFree,
      showDialog,
      closeDialog
    };
  },
  mounted() {
    window.scrollTo(0, 0);
    // Redirect to PaperBoat if query params are missing or invalid
    if (!this.background || !this.etsyUrl) {
      this.$router.replace({ name: 'PaperBoat' });
    }
  }
});
</script>


<style scoped>
.journal-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  z-index: 1;
}
.journal-textarea-overlay::placeholder {
  font-family: 'Quicksand', 'Segoe UI', 'Roboto', sans-serif;
}
.etsy-label {
  text-align: center;
  margin-top: 10px;
  font-size: 0.95rem;
  color: #666;
}
.parisienne-dialog {
  font-family: 'Parisienne', cursive;
  font-size: 2rem;
  display: block;
  margin-bottom: 0;
  color: #444;
}
.quicksand-dialog {
  font-family: 'Quicksand', 'Segoe UI', 'Roboto', sans-serif;
  font-size: 1.1rem;
  color: #333;
}
.set-free-btn {
  background: #ff9800;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 0.6em 1.4em;
  font-size: 1.1rem;
  font-family: 'Segoe UI', 'Roboto', sans-serif;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: background 0.2s;
  margin-top: 4px;
}
.journal-layout p {
  margin-top: 0;
  margin-bottom: 5px;
}
.set-free-btn:hover {
  background: #fb8c00;
}
.journal-image-container {
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background: #fff;
  margin: 0;
  padding: 0;
}
.journal-image-wrapper {
  position: relative;
  display: inline-block;
  max-width: 100vw;
  max-height: calc(100vh - 16px);
}
.journal-image {
  display: block;
  max-width: 60vw;
  max-height: calc(100vh - 16px);
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 0;
  box-shadow: none;
  background: #fff;
  opacity: 1;
  z-index: 1;
  transition: opacity 3s linear;
}

.fade-opacity {
  opacity: 0.3;
}
.journal-textarea-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  resize: none;
  background: transparent;
  color: #000;
  opacity: 1;
  font-size: 1.3rem;
  border: none;
  outline: none;
  padding: 0.75em;
  box-sizing: border-box;
  z-index: 2;
  line-height: 1.6;
  pointer-events: auto;
  font-family: 'Quicksand', 'Segoe UI', 'Roboto', sans-serif !important;
}

.mobile-linebreak {
  display: none;
}

.journal-textarea-overlay::placeholder {
  color: #000;
  opacity: 1;
}

@media (max-width: 600px) {
  .journal-image-container {
    min-height: 220px;
    padding-top: 0 !important;
    padding-bottom: 0;
    margin-top: 0 !important;
    height: auto;
    align-items: flex-start;
    justify-content: center;
  }
  .journal-layout p {
  margin-top: 0;
  margin-bottom: 8px;
}
  .journal-image-wrapper {
    max-width: 100vw;
    max-height: 80vh;
  }
  .journal-image {
    max-width: 92vw;
    max-height: 800vh;
  }
  .journal-textarea-overlay {
    font-size: 1.3rem;
    padding: 0.5em;
    color: #000;
    opacity: 1;
  }
  .etsy-label {
    margin-top: 10px;
    font-size: 0.95rem;
    text-align: center;
    color: #666;
    position: static;
  }
  .about-btn {
    top: 12px;
    right: 10px;
    font-size: 1rem;
    padding: 0.4em 1em;
  }
  .set-free-btn {
    margin-top: 15px;
    font-size: 1rem;
    padding: 0.4em 1em;
  }
  .mobile-linebreak {
    display: block;
    height: 0;
  }
}

</style>

<style scoped>
.fade-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.fade-dialog-content {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.13);
  padding: 2em 1.5em 1.5em 1.5em;
  max-width: 420px;
  text-align: center;
  font-size: 1.1rem;
  color: #333;
  opacity: 0.98;
  animation: fadeInDialog 2.5s cubic-bezier(0.4,0,0.2,1);
}
.fade-dialog-content a {
  color: #ff9800;
  text-decoration: underline;
  word-break: break-all;
}
.close-dialog-btn {
  margin-top: 1.5em;
  background: #ff9800;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 0.5em 1.3em;
  font-size: 1rem;
  font-family: 'Segoe UI', 'Roboto', sans-serif;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: background 0.2s;
}
.close-dialog-btn:hover {
  background: #fb8c00;
}
@keyframes fadeInDialog {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 0.98; transform: scale(1); }
}
.fade-dialog-enter-active, .fade-dialog-leave-active {
  transition: opacity 1.5s;
}
.fade-dialog-enter-from, .fade-dialog-leave-to {
  opacity: 0;
}
.fade-dialog-enter-to, .fade-dialog-leave-from {
  opacity: 1;
}
</style>

<style scoped>
.about-btn {
  position: absolute;
  top: 24px;
  right: 36px;
  z-index: 10;
  background: #ff9800;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 0.5em 1.3em;
  font-size: 1.1rem;
  font-family: 'Segoe UI', 'Roboto', sans-serif;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  text-decoration: none;
  transition: background 0.2s;
}
.about-btn:hover {
  background: #fb8c00;
}
</style>
